import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ChevronRight, ArrowLeft, ArrowRight, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";

const CATEGORY_LABEL = {
  "cuidado-personal": "Cuidado Personal",
  "hogar-limpio": "Hogar Limpio",
  "nutricion-limpia": "Nutrición Limpia",
  "longevidad-celular": "Longevidad",
  general: "General",
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setRelatedProducts([]);
    api.get(`/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        api.get("/articles", { params: { category: data.category } }).then(({ data: rel }) => {
          setRelated(rel.filter((a) => a.slug !== data.slug).slice(0, 2));
        });
        if (data.product_ids && data.product_ids.length) {
          api.get("/products", { params: { ids: data.product_ids.join(",") } })
            .then(({ data: prods }) => setRelatedProducts(prods));
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-display text-3xl font-bold">Artículo no encontrado</h1>
          <Link to="/blog"><Button className="mt-6">Volver a Guías</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center font-mono-tech text-sm text-emerald-400 animate-pulse">Cargando artículo…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <article className="px-4 sm:px-8 lg:px-12 max-w-3xl mx-auto py-10">
        <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:text-emerald-400">Inicio</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-emerald-400">Guías</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-emerald-400 line-clamp-1">{article.title}</span>
        </nav>

        <span className="rounded-full bg-emerald-500/15 border border-emerald-500/40 px-3 py-1 text-[11px] font-mono-tech uppercase tracking-wider text-emerald-300">
          {CATEGORY_LABEL[article.category] || article.category}
        </span>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-4 font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
          {article.title}
        </motion.h1>
        <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
          <span>{article.author}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.read_minutes} min de lectura</span>
        </div>

        <div className="mt-8 overflow-hidden rounded-3xl border border-border">
          <img src={article.cover_image} alt={article.title} className="w-full object-cover aspect-[16/9]" />
        </div>

        <div data-testid="article-content" className="mt-8 space-y-5">
          <p className="text-lg text-muted-foreground leading-relaxed font-medium">{article.excerpt}</p>
          {article.content.split("\n\n").map((para, i) => (
            <p key={i} className="text-base leading-relaxed text-foreground/90">{para}</p>
          ))}
        </div>

        {relatedProducts.length > 0 && (
          <div data-testid="article-products" className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <ShoppingBag className="h-5 w-5 text-emerald-400" />
              <h3 className="font-display text-xl font-bold">Productos mencionados en esta guía</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}

        <div className="mt-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 p-6 text-slate-950">
          <h3 className="font-display text-xl font-extrabold">¿Listo para dar el siguiente paso?</h3>
          <p className="mt-1 text-slate-900/80 text-sm">Explora productos curados con el Sello ZeroToxic para esta categoría.</p>
          <Link to={`/categoria/${article.category}`}>
            <Button className="mt-4 rounded-full bg-slate-950 text-white hover:bg-slate-800 font-bold">
              Ver productos <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="font-display text-xl font-bold mb-5">Sigue leyendo</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {related.map((a) => (
                <Link key={a.id} to={`/blog/${a.slug}`} className="group flex gap-4 rounded-2xl border border-border bg-card p-4 hover:border-emerald-500/50 transition-all">
                  <img src={a.cover_image} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
                  <div>
                    <h4 className="font-medium leading-snug line-clamp-2 group-hover:text-emerald-400 transition-colors">{a.title}</h4>
                    <span className="text-xs text-muted-foreground">{a.read_minutes} min</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Link to="/blog" className="mt-12 inline-flex items-center gap-1 text-sm text-emerald-400 hover:gap-2 transition-all">
          <ArrowLeft className="h-4 w-4" /> Volver a Guías
        </Link>
      </article>
      <Footer />
    </div>
  );
};

export default BlogDetailPage;
